import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, FolderKanban, CheckSquare, Package, UserCircle, 
  TrendingUp, DollarSign, Target, Award, Activity,
  PieChart as PieChartIcon, BarChart3, LineChart, Download, Calendar, Filter
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart as RechartsLineChart, Line, Area, AreaChart
} from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palettes
const COLORS = ['#DC2626', '#EA580C', '#D97706', '#CA8A04', '#65A30D', '#16A34A', '#0D9488', '#0891B2', '#0284C7', '#2563EB', '#7C3AED', '#C026D3'];
const STAGE_COLORS = {
  'New': '#3B82F6',
  'Qualification': '#F59E0B',
  'Proposal': '#8B5CF6',
  'Negotiation': '#F97316',
  'Won': '#10B981',
  'Lost': '#EF4444'
};
const STATUS_COLORS = {
  'Planning': '#3B82F6',
  'In Progress': '#0EA5E9',
  'On Hold': '#F59E0B',
  'Completed': '#10B981',
  'Delayed': '#EF4444'
};
const PRIORITY_COLORS = {
  'Low': '#94A3B8',
  'Medium': '#F59E0B',
  'High': '#EF4444'
};

const formatCurrency = (amount) => {
  if (!amount) return "₹0";
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {typeof entry.value === 'number' && entry.value > 1000 
              ? formatCurrency(entry.value) 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AnalyticsPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply date range filter to data
  const applyDateFilter = (items, dateField = "created_at") => {
    if (dateRange === "all" && !startDate && !endDate) return items;
    
    const now = new Date();
    let start = startDate ? new Date(startDate) : null;
    let end = endDate ? new Date(endDate) : now;
    
    // Preset date ranges
    if (dateRange === "7d") {
      start = new Date(now - 7 * 24 * 60 * 60 * 1000);
    } else if (dateRange === "30d") {
      start = new Date(now - 30 * 24 * 60 * 60 * 1000);
    } else if (dateRange === "90d") {
      start = new Date(now - 90 * 24 * 60 * 60 * 1000);
    } else if (dateRange === "1y") {
      start = new Date(now - 365 * 24 * 60 * 60 * 1000);
    }
    
    if (!start) return items;
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= start && itemDate <= end;
    });
  };

  // Export data to CSV
  const exportToCSV = (dataArray, filename, columns) => {
    if (!dataArray || dataArray.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = columns.map(col => col.label).join(",");
    const rows = dataArray.map(item => 
      columns.map(col => {
        const value = item[col.key];
        // Escape commas and quotes in values
        if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? "";
      }).join(",")
    );
    
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success(`Exported ${dataArray.length} records to ${filename}.csv`);
  };

  // Export summary data
  const exportSummary = () => {
    if (!data) return;
    
    const summaryData = [
      { metric: "Total Leads", value: data.summary.total_leads },
      { metric: "Total Projects", value: data.summary.total_projects },
      { metric: "Total Tasks", value: data.summary.total_tasks },
      { metric: "Total Vendors", value: data.summary.total_vendors },
      { metric: "Total Employees", value: data.summary.total_employees },
      { metric: "Conversion Rate", value: `${data.summary.conversion_rate}%` },
      { metric: "Avg Project Completion", value: `${data.summary.avg_project_completion}%` },
      { metric: "Task Completion Rate", value: `${data.summary.task_completion_rate}%` },
      { metric: "Total Budget", value: data.summary.total_budget },
      { metric: "Total Invoiced", value: data.summary.total_invoiced },
      { metric: "Total Received", value: data.summary.total_received },
      { metric: "Total Expenses", value: data.summary.total_expenses },
      { metric: "Outstanding", value: data.summary.outstanding }
    ];
    
    exportToCSV(summaryData, "analytics_summary", [
      { key: "metric", label: "Metric" },
      { key: "value", label: "Value" }
    ]);
  };

  // Export leads by stage
  const exportLeadsData = () => {
    if (!data?.leads?.by_stage) return;
    exportToCSV(data.leads.by_stage, "leads_by_stage", [
      { key: "name", label: "Stage" },
      { key: "value", label: "Count" }
    ]);
  };

  // Export projects by status
  const exportProjectsData = () => {
    if (!data?.projects?.by_status) return;
    exportToCSV(data.projects.by_status, "projects_by_status", [
      { key: "name", label: "Status" },
      { key: "value", label: "Count" }
    ]);
  };

  // Export monthly trends
  const exportTrendsData = () => {
    if (!data?.trends) return;
    exportToCSV(data.trends, "monthly_trends", [
      { key: "month", label: "Month" },
      { key: "leads", label: "Leads" },
      { key: "projects", label: "Projects" },
      { key: "expenses", label: "Expenses" }
    ]);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brillien-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-center bg-slate-50 dark:bg-slate-900 min-h-screen">
        <p className="text-slate-500 dark:text-slate-400">Failed to load analytics data</p>
      </div>
    );
  }

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Analytics & Reports
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Comprehensive insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="border-slate-300 dark:border-slate-400 dark:text-slate-300 dark:hover:bg-slate-800"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40 dark:border-slate-400 dark:bg-slate-800 dark:text-slate-200" data-testid="date-range-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={exportSummary}
            className="bg-brillien-600 hover:bg-brillien-700"
            data-testid="export-summary-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Summary
          </Button>
        </div>
      </div>

      {/* Custom Date Range Filter */}
      {(showFilters || dateRange === "custom") && (
        <Card className="p-4 mb-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-end gap-4">
            <div>
              <Label className="text-sm text-slate-600 dark:text-slate-400">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600 dark:text-slate-400">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
                data-testid="end-date-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => { setStartDate(""); setEndDate(""); setDateRange("all"); }}
              className="text-slate-600 dark:text-slate-300 dark:border-slate-400"
            >
              Clear
            </Button>
          </div>
        </Card>
      )}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <KPICard 
          title="Total Leads" 
          value={data.summary.total_leads} 
          icon={Users} 
          color="blue"
          subtitle={`${data.summary.conversion_rate}% converted`}
        />
        <KPICard 
          title="Projects" 
          value={data.summary.total_projects} 
          icon={FolderKanban} 
          color="purple"
          subtitle={`${data.summary.avg_project_completion}% avg completion`}
        />
        <KPICard 
          title="Tasks" 
          value={data.summary.total_tasks} 
          icon={CheckSquare} 
          color="orange"
          subtitle={`${data.summary.task_completion_rate}% completed`}
        />
        <KPICard 
          title="Total Budget" 
          value={formatCurrency(data.summary.total_budget)} 
          icon={DollarSign} 
          color="green"
          subtitle="All projects"
        />
        <KPICard 
          title="Revenue" 
          value={formatCurrency(data.summary.total_received)} 
          icon={TrendingUp} 
          color="emerald"
          subtitle={`${formatCurrency(data.summary.outstanding)} outstanding`}
        />
        <KPICard 
          title="Expenses" 
          value={formatCurrency(data.summary.total_expenses)} 
          icon={Activity} 
          color="red"
          subtitle="Total spent"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-white p-1 rounded-lg shadow-sm border border-slate-200">
          <TabsTrigger value="overview" className="data-[state=active]:bg-brillien-50 data-[state=active]:text-brillien-700">
            <BarChart3 className="h-4 w-4 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="leads" className="data-[state=active]:bg-brillien-50 data-[state=active]:text-brillien-700">
            <Users className="h-4 w-4 mr-2" />Leads
          </TabsTrigger>
          <TabsTrigger value="projects" className="data-[state=active]:bg-brillien-50 data-[state=active]:text-brillien-700">
            <FolderKanban className="h-4 w-4 mr-2" />Projects
          </TabsTrigger>
          <TabsTrigger value="finance" className="data-[state=active]:bg-brillien-50 data-[state=active]:text-brillien-700">
            <DollarSign className="h-4 w-4 mr-2" />Finance
          </TabsTrigger>
          <TabsTrigger value="team" className="data-[state=active]:bg-brillien-50 data-[state=active]:text-brillien-700">
            <UserCircle className="h-4 w-4 mr-2" />Team
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={exportTrendsData}
              className="border-slate-300"
              data-testid="export-trends-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Trends
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Trends */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <LineChart className="h-5 w-5 text-brillien-600" />
                Monthly Trends
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="leads" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Leads" />
                  <Area type="monotone" dataKey="projects" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} name="Projects" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Lead Stage Distribution */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-brillien-600" />
                Lead Pipeline
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.leads.by_stage}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {data.leads.by_stage.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STAGE_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Project Status */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-brillien-600" />
                Project Status
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.projects.by_status} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#94A3B8" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {data.projects.by_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Task Priority Distribution */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-brillien-600" />
                Task Priority
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.tasks.by_priority}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {data.tasks.by_priority.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads">
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={exportLeadsData}
              className="border-slate-300"
              data-testid="export-leads-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Leads Data
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Stage Funnel */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Lead Stage Funnel</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.leads.by_stage}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {data.leads.by_stage.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STAGE_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Lead Sources */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Lead Sources</h3>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={data.leads.by_source}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {data.leads.by_source.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Lead Categories */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Lead Categories</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.leads.by_category}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#DC2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Conversion Metrics */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Conversion Metrics</h3>
              <div className="grid grid-cols-4 gap-6">
                <MetricBox 
                  label="Total Leads" 
                  value={data.summary.total_leads} 
                  color="blue"
                />
                <MetricBox 
                  label="Qualified" 
                  value={data.leads.by_stage.find(s => s.name === 'Qualification')?.value || 0} 
                  color="amber"
                />
                <MetricBox 
                  label="Won" 
                  value={data.leads.by_stage.find(s => s.name === 'Won')?.value || 0} 
                  color="emerald"
                />
                <MetricBox 
                  label="Conversion Rate" 
                  value={`${data.summary.conversion_rate}%`} 
                  color="purple"
                />
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={exportProjectsData}
              className="border-slate-300"
              data-testid="export-projects-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Projects Data
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Project Status Distribution */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Project Status</h3>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={data.projects.by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {data.projects.by_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Project Types */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Project Types</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.projects.by_type} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#94A3B8" width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Project Metrics */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Project Metrics</h3>
              <div className="grid grid-cols-5 gap-6">
                <MetricBox 
                  label="Total Projects" 
                  value={data.summary.total_projects} 
                  color="purple"
                />
                <MetricBox 
                  label="In Progress" 
                  value={data.projects.by_status.find(s => s.name === 'In Progress')?.value || 0} 
                  color="blue"
                />
                <MetricBox 
                  label="Completed" 
                  value={data.projects.by_status.find(s => s.name === 'Completed')?.value || 0} 
                  color="emerald"
                />
                <MetricBox 
                  label="On Hold" 
                  value={data.projects.by_status.find(s => s.name === 'On Hold')?.value || 0} 
                  color="amber"
                />
                <MetricBox 
                  label="Avg Completion" 
                  value={`${data.summary.avg_project_completion}%`} 
                  color="cyan"
                />
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Finance Tab */}
        <TabsContent value="finance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Financial Summary */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Financial Overview</h3>
              <div className="grid grid-cols-5 gap-6">
                <MetricBox 
                  label="Total Budget" 
                  value={formatCurrency(data.summary.total_budget)} 
                  color="blue"
                />
                <MetricBox 
                  label="Total Invoiced" 
                  value={formatCurrency(data.summary.total_invoiced)} 
                  color="purple"
                />
                <MetricBox 
                  label="Amount Received" 
                  value={formatCurrency(data.summary.total_received)} 
                  color="emerald"
                />
                <MetricBox 
                  label="Outstanding" 
                  value={formatCurrency(data.summary.outstanding)} 
                  color="amber"
                />
                <MetricBox 
                  label="Total Expenses" 
                  value={formatCurrency(data.summary.total_expenses)} 
                  color="red"
                />
              </div>
            </Card>

            {/* Expenses by Status */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Expenses by Status</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.finance.expenses_by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                  >
                    {data.finance.expenses_by_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Expenses by Payment Mode */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Expenses by Payment Mode</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.finance.expenses_by_mode}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Monthly Expense Trend */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Monthly Expense Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsLineChart data={data.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} name="Expenses" />
                </RechartsLineChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Task Status */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Task Status Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.tasks.by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {data.tasks.by_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Tasks by Assignee */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Tasks by Team Member</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.tasks.by_assignee} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#94A3B8" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#DC2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Team by Department */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Team by Department</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.team.by_department}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {data.team.by_department.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Vendor Categories */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Vendors by Category</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.vendors.by_category}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Team Metrics */}
            <Card className="p-6 border-0 shadow-md rounded-xl bg-white lg:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">Team Performance Metrics</h3>
              <div className="grid grid-cols-5 gap-6">
                <MetricBox 
                  label="Total Employees" 
                  value={data.summary.total_employees} 
                  color="purple"
                />
                <MetricBox 
                  label="Total Vendors" 
                  value={data.summary.total_vendors} 
                  color="cyan"
                />
                <MetricBox 
                  label="Total Tasks" 
                  value={data.summary.total_tasks} 
                  color="orange"
                />
                <MetricBox 
                  label="Completed Tasks" 
                  value={data.tasks.by_status.find(s => s.name === 'Completed')?.value || 0} 
                  color="emerald"
                />
                <MetricBox 
                  label="Completion Rate" 
                  value={`${data.summary.task_completion_rate}%`} 
                  color="blue"
                />
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// KPI Card Component
function KPICard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
    green: "bg-green-100 text-green-600",
    emerald: "bg-emerald-100 text-emerald-600",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-600",
    cyan: "bg-cyan-100 text-cyan-600",
  };

  return (
    <Card className="p-5 border-0 shadow-md rounded-xl bg-white hover:shadow-lg transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </Card>
  );
}

// Metric Box Component
function MetricBox({ label, value, color }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    green: "bg-green-50 border-green-200 text-green-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    cyan: "bg-cyan-50 border-cyan-200 text-cyan-700",
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]} text-center`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs mt-1 opacity-80">{label}</p>
    </div>
  );
}
